"use client";

import { c as _c } from "react/compiler-runtime";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchStreamLink, loggerLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import posthog from "posthog-js";
import { cache } from "react";
import SuperJSON from "superjson";
import { getStoredAttributionCode } from "~/lib/attribution";
import { authClient } from "~/lib/auth/client";
import { getStoredLandingUtm } from "~/lib/utm";
import type { AppRouter } from "~/server/api/root";
import { createQueryClient } from "./query-client";

// Server: cache() scopes the QueryClient per request (deterministic for cacheComponents).
// Browser: module-level singleton.
const getServerQueryClient = cache(createQueryClient);
let clientQueryClientSingleton: QueryClient | undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    return getServerQueryClient();
  }
  clientQueryClientSingleton ??= createQueryClient();
  return clientQueryClientSingleton;
};
export const api = createTRPCReact<AppRouter>();

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

// The tRPC client is stateless — auth is fetched dynamically in the headers
// callback below, not at creation time. A module-level singleton avoids
// creating a fresh (uncacheable) instance on every server render, which
// Next.js cacheComponents would otherwise flag as blocking.
const trpcClientSingleton = api.createClient({
  links: [loggerLink({
    enabled: op => process.env.NODE_ENV === "development" || op.direction === "down" && op.result instanceof Error
  }), httpBatchStreamLink({
    transformer: SuperJSON,
    url: `${getBaseUrl()}/api/trpc`,
    headers: async ({
      opList
    }) => {
      const headers = new Headers();
      headers.set("x-trpc-source", "nextjs-react");
      // Forward the browser's PostHog identity so the backend can stitch
      // server-mirrored analytics events to the same person/session.
      try {
        const anonymousId = posthog.get_distinct_id();
        if (anonymousId) {
          headers.set("x-analytics-anonymous-id", anonymousId);
        }
        const sessionId = posthog.get_session_id();
        if (sessionId) {
          headers.set("x-analytics-session-id", sessionId);
        }
      } catch {
        // proceed without the headers if PostHog is unavailable
      }
      // The session's landing campaign params. Sent on every call so any
      // server-mirrored analytics event can carry utm_* on the EVENT, which
      // is what Marketing Analytics conversion goals read (posthog-js only
      // covers the person's $initial_utm_*).
      try {
        const utm = getStoredLandingUtm();
        if (utm) {
          headers.set("x-analytics-utm", utm);
        }
      } catch {
        // proceed without the header if storage is unavailable
      }
      const shouldAttachAttribution = opList.some(op => op.path === "user.getUser");
      if (shouldAttachAttribution) {
        try {
          const code = getStoredAttributionCode();
          if (code) {
            headers.set("x-attribution-code", code);
          }
        } catch {
          // proceed without the header if storage is unavailable
        }
      }
      // Include the Supabase access token so the server can authenticate
      // requests made immediately after verifyOtp, before the session
      // cookie is available in the browser's cookie jar.
      try {
        const accessToken = await authClient.getAccessToken();
        if (accessToken) {
          headers.set("Authorization", `Bearer ${accessToken}`);
        }
      } catch {
        // proceed without the header if session is unavailable
      }
      return headers;
    }
  })]
});
export function TRPCReactProvider(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getQueryClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const queryClient = t0;
  let t1;
  if ($[1] !== props.children) {
    t1 = <QueryClientProvider client={queryClient}><api.Provider client={trpcClientSingleton} queryClient={queryClient}>{props.children}</api.Provider></QueryClientProvider>;
    $[1] = props.children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function getBaseUrl() {
  if (typeof window !== "undefined") return window.location.origin;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}